import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, PackageSearch, ShoppingBag, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Menu as MenuIcon, UserRound } from "lucide-react";
import { Show } from "@clerk/react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DynIcon } from "@/components/store/Icon";
import { api } from "@/lib/api";
import { useCart, useLogo, useSite } from "@/lib/store";
import { FaInstagram, FaFacebookF, FaWhatsapp, FaYoutube, FaXTwitter, FaSpotify, FaPinterestP, FaLinkedinIn, FaThreads, FaTelegram } from "react-icons/fa6";

export const SOCIALS = {
  instagram: ["Instagram", FaInstagram], facebook: ["Facebook", FaFacebookF], whatsapp: ["WhatsApp", FaWhatsapp], youtube: ["YouTube", FaYoutube],
  x: ["X (Twitter)", FaXTwitter], spotify: ["Spotify", FaSpotify], pinterest: ["Pinterest", FaPinterestP], linkedin: ["LinkedIn", FaLinkedinIn],
  threads: ["Threads", FaThreads], telegram: ["Telegram", FaTelegram],
};

const WRAP = "max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10";

export function TopBar() {
  const { site } = useSite();
  const msgs = site?.topbar || [];
  return (
    <div className="bg-black" data-testid="top-bar">
      <div className={`${WRAP} flex justify-center sm:justify-between items-center h-8`}>
        {msgs.map((m, i) => (
          <span key={i} className={`label-caps text-[10px] text-[#a3a3a3] items-center gap-2 ${i === 0 ? "flex" : "hidden sm:flex"}`} data-testid={`top-bar-msg-${i}`}>
            <DynIcon name={m.icon} className="w-3.5 h-3.5 text-red" strokeWidth={1.8} /> {m.text}
          </span>
        ))}
      </div>
    </div>
  );
}

function SearchBox({ mobile = false }) {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form onSubmit={submit} className={`flex w-full border-2 border-black bg-white ${mobile ? "rounded-full overflow-hidden font-arial" : ""}`} data-testid={mobile ? "mobile-search-form" : "header-search-form"}>
      <div className="flex items-center flex-1 px-3 gap-2">
        <Search className="w-4 h-4 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search artist, title, catalogue no. or track…"
          className="w-full h-10 outline-none text-sm bg-transparent" data-testid={mobile ? "mobile-search-input" : "header-search-input"} />
      </div>
      <button type="submit" className={`bg-black text-white px-5 sm:px-7 text-xs font-bold hover:bg-[#dc1f26] transition-colors ${mobile ? "rounded-full tracking-[0.08em]" : "tracking-[0.15em]"}`} data-testid={mobile ? "mobile-search-button" : "header-search-button"}>
        SEARCH
      </button>
    </form>
  );
}

function MobileDrawer() {
  const { site } = useSite();
  const LOGO = useLogo();
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const loc = useLocation();
  const genres = (site?.menu || []).filter((m) => !m.hidden);
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname, loc.search]);
  useEffect(() => {
    if (open && !cats.length) api.get("/gear-categories").then((r) => setCats(r.data)).catch(() => {});
  }, [open, cats.length]);
  const top = "block py-3.5 text-[14px] font-bold uppercase tracking-[0.05em] border-b border-[#eee]";
  const sub = "block py-2.5 pl-3 text-[14.5px] text-[#333] hover:text-red";
  const trig = "py-3.5 text-[14px] font-bold uppercase tracking-[0.05em] hover:no-underline";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="md:hidden -ml-1 p-1" aria-label="Open menu" data-testid="mobile-menu-button">
        <MenuIcon className="w-6 h-6" strokeWidth={1.75} />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[86vw] max-w-sm p-0 overflow-y-auto font-arial" data-testid="mobile-menu-drawer">
          <SheetTitle className="px-5 pt-5 pb-3"><img src={LOGO} alt="Vinyl Record Vault" className="h-9 w-auto" /></SheetTitle>
          <div className="px-5 pb-10">
            <Link to="/shop" className={top} data-testid="mobile-menu-all-records">All records</Link>
            <Link to="/account" className={top} data-testid="mobile-menu-account"><Show when="signed-in">My account</Show><Show when="signed-out">Sign in / Create account</Show></Link>
            <Link to="/about-us" className={`${top} order-last`} data-testid="mobile-menu-about">About us</Link>
            <Accordion type="single" collapsible>
              <AccordionItem value="mood" className="border-[#eee]">
                <AccordionTrigger className={trig} data-testid="mobile-menu-mood">Shop by mood</AccordionTrigger>
                <AccordionContent>
                  {(site?.mood_groups || []).map((g) => (
                    <div key={g.id} className="mb-3">
                      <p className="label-caps text-[10px] text-red pl-3 pt-2">{g.name}</p>
                      {g.moods.map((m) => <Link key={m.id} to={`/shop?mood=${m.id}`} className={sub} data-testid={`mobile-menu-mood-${m.id}`}>{m.name}</Link>)}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="gear" className="border-[#eee]">
                <AccordionTrigger className={trig} data-testid="mobile-menu-gear">Turntables & Accessories</AccordionTrigger>
                <AccordionContent>
                  <Link to="/turntables-accessories" className={`${sub} font-bold`} data-testid="mobile-menu-gear-all">Shop all</Link>
                  {cats.map((c) => <Link key={c.name} to={`/turntables-accessories?cat=${encodeURIComponent(c.name)}`} className={sub} data-testid={`mobile-menu-gear-${c.name.replace(/\W+/g, "-").toLowerCase()}`}>{c.name}</Link>)}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="genre" className="border-[#eee]">
                <AccordionTrigger className={trig} data-testid="mobile-menu-genres">Genres</AccordionTrigger>
                <AccordionContent>
                  {genres.map((m) => <MenuLink key={m.id} m={m} className={sub} testid={`mobile-menu-genre-${m.id}`} />)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function Header() {
  const { count } = useCart();
  const LOGO = useLogo();
  return (
    <header className="bg-white">
      <div className={`${WRAP} py-4 grid grid-cols-2 md:grid-cols-[auto_1fr_auto] items-center gap-4 md:gap-10`}>
        <div className="flex items-center gap-3">
          <MobileDrawer />
          <Link to="/" data-testid="header-logo-link"><img src={LOGO} alt="Vinyl Record Vault" className="h-9 sm:h-11 w-auto" /></Link>
        </div>
        <div className="hidden md:block max-w-3xl w-full md:mx-auto"><SearchBox /></div>
        <div className="flex items-center justify-end gap-4 sm:gap-7">
          <div className="hidden lg:block text-center leading-tight" data-testid="header-currency-label">
            <div className="label-caps text-[10px] text-gray-500">Prices in</div>
            <div className="text-sm font-bold">₹ INR</div>
          </div>
          <Link to="/account" className="hidden md:flex items-center gap-2 text-sm hover:text-red transition-colors" data-testid="header-account-link">
            <UserRound className="w-5 h-5" strokeWidth={1.5} /> <span className="text-sm whitespace-nowrap"><Show when="signed-in">My account</Show><Show when="signed-out">Sign in</Show></span>
          </Link>
          <Link to="/track" className="flex items-center gap-2 text-sm hover:text-red transition-colors" data-testid="header-track-order-link">
            <PackageSearch className="w-5 h-5" strokeWidth={1.5} /> <span className="text-xs sm:text-sm whitespace-nowrap">Track order</span>
          </Link>
          <Link to="/basket" className="relative flex items-center gap-2 text-sm hover:text-red transition-colors" data-testid="header-basket-link">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} /> <span className="text-xs sm:text-sm">Basket</span>
            {count > 0 && <span className="absolute -top-2 left-3 bg-red text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center" data-testid="header-basket-count">{count}</span>}
          </Link>
        </div>
      </div>
    </header>
  );
}

const navCls = "nav-link text-[13px] font-bold tracking-[0.06em] uppercase whitespace-nowrap hover:text-red";

const menuHref = (m) => (m.type === "link" ? m.value : `/shop?genre=${encodeURIComponent(m.value)}`);

function MenuLink({ m, className, testid, onClick }) {
  return /^https?:/.test(m.value) && m.type === "link"
    ? <a href={m.value} className={className} data-testid={testid}>{m.label}</a>
    : <Link to={menuHref(m)} onClick={onClick} className={className} data-testid={testid}>{m.label}</Link>;
}

export function MobileStickyBar() {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const items = (site?.menu || []).filter((m) => !m.hidden);
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname, loc.search]);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div className="md:hidden sticky top-0 z-40 bg-white border-b border-[#e5e5e5] shadow-[0_6px_12px_-10px_rgba(0,0,0,0.25)]" data-testid="mobile-sticky-bar">
      <div className={`${WRAP} pt-2 pb-2 space-y-2`}>
        <SearchBox mobile />
        <button type="button" onClick={() => setOpen(!open)} data-testid="mobile-genre-toggle"
          className={`w-full h-10 px-4 flex items-center justify-between rounded-full font-arial text-[13px] font-bold tracking-[0.04em] uppercase border border-black transition-colors ${open ? "bg-red text-white border-red" : "bg-white"}`}>
          Shop by genre <Chevron className="w-4 h-4" />
        </button>
      </div>
      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 bg-white border border-[#e5e5e5] rounded-2xl shadow-[0_12px_24px_-12px_rgba(0,0,0,0.25)] max-h-[65vh] overflow-y-auto font-arial" data-testid="mobile-genre-menu">
          <ul className={`${WRAP} py-4 grid grid-cols-2 gap-x-6`}>
            {items.map((m) => (
              <li key={m.id} className="border-b border-[#f0f0f0]">
                <MenuLink m={m} onClick={() => setOpen(false)} className="block py-3 text-[14.5px] text-[#333] hover:text-red" testid={`mobile-genre-${m.id}`} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MoodPanel({ groups, onClose }) {
  return (
    <div className="absolute left-0 right-0 top-full z-50 bg-white border-b border-[#e5e5e5] shadow-[0_12px_24px_-12px_rgba(0,0,0,0.18)]" data-testid="mood-mega-menu">
      <div className={`${WRAP} py-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-10`}>
        {groups.map((g) => (
          <div key={g.id} data-testid={`mood-group-col-${g.id}`}>
            <h4 className="font-head font-bold text-[17px] pb-3 border-b border-[#e5e5e5] mb-4">{g.name}</h4>
            <ul className="space-y-2.5">
              {g.moods.map((m) => (
                <li key={m.id}>
                  <Link to={`/shop?mood=${m.id}`} onClick={onClose} className="block text-[14.5px] text-[#444] hover:text-red transition-colors" data-testid={`nav-mood-${m.id}`}>
                    {m.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

const hoverable = () => window.matchMedia("(hover: hover)").matches;

function GearMenu({ onEnter }) {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const loc = useLocation();
  useEffect(() => {
    api.get("/gear-categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname, loc.search]);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div className="relative md:self-stretch flex" onMouseEnter={() => { onEnter?.(); setOpen(true); }} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => { onEnter?.(); setOpen((o) => (hoverable() ? true : !o)); }} data-testid="nav-gear"
        className={`h-12 md:h-auto -my-px px-3 max-md:pl-0 flex items-center gap-1.5 text-[13px] font-bold tracking-[0.06em] uppercase whitespace-nowrap transition-colors ${open ? "bg-red text-white max-md:pl-3" : "hover:text-red"}`}>
        Turntables & Accessories <Chevron className="w-3.5 h-3.5" />
      </button>
      {open && (
        <ul className="absolute left-0 top-full z-50 min-w-[240px] bg-white border border-[#e5e5e5] shadow-[0_12px_24px_-12px_rgba(0,0,0,0.25)] py-2 font-arial" data-testid="nav-gear-menu">
          <li><Link to="/turntables-accessories" className="block px-4 py-2.5 text-sm font-bold hover:text-red" data-testid="nav-gear-all">Shop all</Link></li>
          {cats.map((c) => (
            <li key={c.name}>
              <Link to={`/turntables-accessories?cat=${encodeURIComponent(c.name)}`} className="block px-4 py-2.5 text-sm text-[#333] hover:text-red" data-testid={`nav-gear-cat-${c.name.replace(/\W+/g, "-").toLowerCase()}`}>
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function GenreRow() {
  const { site } = useSite();
  const ref = useRef(null);
  const items = (site?.menu || []).filter((m) => !m.hidden);
  const [edges, setEdges] = useState({ start: true, end: false });
  const scroll = (dir) => ref.current?.scrollBy({ left: 320 * dir, behavior: "smooth" });
  const onScroll = () => {
    const el = ref.current;
    if (el) setEdges({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 });
  };
  useEffect(() => {
    onScroll();
    const ro = new ResizeObserver(onScroll);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [items.length]);
  return (
    <nav className="hidden md:block bg-[#5c0f14] text-white" data-testid="genre-menu">
      <div className={`${WRAP} flex items-center h-11 gap-3`}>
        <button onClick={() => scroll(-1)} disabled={edges.start} className="shrink-0 w-7 h-7 grid place-items-center hover:text-[#ffb3b8] disabled:opacity-25 disabled:hover:text-inherit" aria-label="Previous genres" data-testid="nav-genre-scroll-left-btn">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div ref={ref} onScroll={onScroll} className="flex-1 min-w-0 flex gap-7 overflow-x-auto scrollbar-none" data-testid="nav-genre-scroller">
          {items.map((m) => <MenuLink key={m.id} m={m} className="text-[12.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap py-3 hover:text-[#ffb3b8] transition-colors" testid={`nav-item-${m.id}`} />)}
        </div>
        <button onClick={() => scroll(1)} disabled={edges.end} className="shrink-0 w-7 h-7 grid place-items-center hover:text-[#ffb3b8] disabled:opacity-25 disabled:hover:text-inherit" aria-label="More genres" data-testid="nav-genre-scroll-btn">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </nav>
  );
}

export function MenuRow() {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname, loc.search]);
  const close = () => setOpen(false);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <nav className="hidden md:block bg-white border-y border-[#e5e5e5] relative" data-testid="main-menu" onMouseLeave={() => setOpen(false)}>
      <div className={`${WRAP} flex flex-wrap md:flex-nowrap items-center md:h-12 gap-x-5 lg:gap-x-7`}>
        <Link to="/shop" onMouseEnter={close} className={`${navCls} leading-[48px] md:leading-normal`} data-testid="nav-all-records">All records</Link>
        <Link to="/shop?sort=new&view=new" onMouseEnter={close} className={`${navCls} hidden md:block`} data-testid="nav-new-in">New in</Link>
        <button type="button" onClick={() => setOpen((o) => (hoverable() ? true : !o))} onMouseEnter={() => setOpen(true)} data-testid="nav-shop-by-mood"
          className={`h-12 md:h-auto md:self-stretch -my-px px-3 flex items-center gap-1.5 text-[13px] font-bold tracking-[0.06em] uppercase whitespace-nowrap transition-colors ${open ? "bg-red text-white" : "hover:text-red"}`}>
          Shop by mood <Chevron className="w-3.5 h-3.5" />
        </button>
        <GearMenu onEnter={close} />
        <span className="flex-1 hidden md:block" />
        <Link to="/blog" onMouseEnter={close} className={`${navCls} hidden md:block`} data-testid="nav-blog">Blog</Link>
        <Link to="/shop?sale=1" onMouseEnter={close} className={`${navCls} text-red hidden md:block`} data-testid="nav-sale">Sale</Link>
      </div>
      {open && <MoodPanel groups={site?.mood_groups || []} onClose={close} />}
    </nav>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className="label-caps text-[11px] text-red mb-4">{title}</h4>
      <ul className="space-y-2.5 text-sm text-[#bdbdbd]">{children}</ul>
    </div>
  );
}

const FL = ({ to, children, id }) => (
  <li><Link to={to} className="hover:text-white transition-colors" data-testid={id}>{children}</Link></li>
);

export function Footer() {
  const LOGO = useLogo();
  const { site } = useSite();
  const genres = (site?.menu || []).filter((m) => !m.hidden && m.type === "genre").slice(0, 10);
  const moods = (site?.mood_groups || []).flatMap((g) => g.moods).slice(0, 10);
  return (
    <footer className="bg-black text-white mt-24" data-testid="site-footer">
      <div className={`${WRAP} py-16 grid gap-12 sm:grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr_1fr_1fr]`}>
        <div>
          <div className="bg-white inline-block p-3"><img src={LOGO} alt="Vinyl Record Vault" className="h-10" /></div>
          <p className="mt-6 text-sm text-[#bdbdbd] leading-relaxed max-w-sm" data-testid="footer-about">{site?.footer_about}</p>
          <p className="mt-6 label-caps text-[10px] text-[#8a8a8a]" data-testid="footer-note">{site?.footer_note}</p>
          {site?.socials?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid="footer-socials">
              {site.socials.filter((s) => SOCIALS[s.platform]).map((s) => {
                const [label, Ico] = SOCIALS[s.platform];
                return (
                  <a key={s.platform + s.url} href={s.url} target="_blank" rel="noreferrer" aria-label={label} title={label}
                    className="w-10 h-10 rounded-full border border-[#444] grid place-items-center text-[#d4d4d4] hover:bg-red hover:border-red hover:text-white transition-colors" data-testid={`footer-social-${s.platform}`}>
                    <Ico className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <FooterCol title="Genres">{genres.map((g) => <FL key={g.id} to={`/shop?genre=${encodeURIComponent(g.value)}`}>{g.label}</FL>)}</FooterCol>
        <FooterCol title="Shop by mood">{moods.map((m) => <FL key={m.id} to={`/shop?mood=${m.id}`}>{m.name}</FL>)}</FooterCol>
        <FooterCol title="The vault">
          <FL to="/shop" id="footer-full-catalogue">Full catalogue</FL>
          <FL to="/shop?sort=new&view=new">New arrivals</FL>
          <FL to="/shop?sale=1">Sale</FL>
          <FL to="/blog">Blog</FL>
          <FL to="/track" id="footer-track-order">Track your order</FL>
        </FooterCol>
        <FooterCol title="Help">
          <FL to="/about-us" id="footer-about-link">About Us</FL>
          <FL to="/contact-us" id="footer-contact">Contact Us</FL>
          <FL to="/terms-and-conditions#shipping-return-and-refund-policy" id="footer-shipping-policy">Shipping Policy</FL>
          <FL to="/terms-and-conditions#eligibility-for-a-return-replacement-or-refund" id="footer-returns">Returns and Refund Policy</FL>
          <FL to="/terms-and-conditions" id="footer-terms">Terms and Conditions</FL>
        </FooterCol>
      </div>
      <div className="border-t border-[#222]">
        <div className={`${WRAP} py-5 text-xs text-[#777] font-arial`}>© {new Date().getFullYear()} Vinyl Record Vault. New vinyl pressings only.</div>
      </div>
    </footer>
  );
}

export default function StoreLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--vrv-cream)]">
      <TopBar />
      <Header />
      <GenreRow />
      <MobileStickyBar />
      <MenuRow />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

export { WRAP };
